import React, { useCallback, useState } from "react";
import { Card, Flex, SlideoutDrawerV2, Text, SelectableCards, Button } from "@jsq/ds/core";
import {
  UnifiedPayeeWorkflow,
  type PayeeType,
} from "../../../PayeeWorkflows/UnifiedPayeeWorkflow";
import { PayableDocument } from "../../types";
import { PayeeObjectType } from "../PayeeFilter/types";
import {
  slideoutDrawerContainer,
  bodySizeContainer,
  customHeader,
  headerTitleOverlay,
} from "./styles_vanilla.css";
import type { PayeeBaseSupportingDocument } from "../../../PayeeWorkflows/types";

const isPayeeType = (v: string | number): v is PayeeType =>
  typeof v === "string" && (v === "Vendor" || v === "Entity" || v === "Investment");

interface PayeeCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPayeeCreated: (payee?: PayeeObjectType | null) => void;
  arenaId?: string;
  arenaName?: string;
  initialDocument?: PayableDocument;
}

export const PayeeCreationModal: React.FC<PayeeCreationModalProps> = ({
  isOpen,
  onClose,
  onPayeeCreated,
  arenaId,
  arenaName,
  initialDocument,
}) => {
  const [selectedType, setSelectedType] = useState<PayeeType | null>(null);
  const [selectedPayeeType, setSelectedPayeeType] = useState<PayeeType | null>(null);
  const [stepData, setStepData] = useState<{
    currentStep: number;
    totalSteps: number;
    canGoBack: boolean;
    canGoForward: boolean;
    isLoading: boolean;
    submitText: string;
  } | null>(null);

  const handleStepChange = useCallback((data: typeof stepData) => {
    setStepData(data);
  }, []);

  const handleContinueFromTypeSelection = useCallback(() => {
    if (selectedPayeeType) {
      setSelectedType(selectedPayeeType);
    }
  }, [selectedPayeeType]);

  const handleExit = useCallback(() => {
    setSelectedType(null);
    setSelectedPayeeType(null);
    onClose();
  }, [onClose]);

  const handlePayeeCreationSuccess = useCallback(
    (createdPayee?: unknown) => {
      setSelectedType(null);
      setSelectedPayeeType(null);
      onClose();
      // Pass the created payee data to trigger a refetch and potentially auto-select
      onPayeeCreated((createdPayee as PayeeObjectType) ?? null);
    },
    [onClose, onPayeeCreated],
  );

  // If a workflow is selected, render it in a bottom-sheet style modal
  if (selectedType) {
    // In payable inline flow, force a re-upload by stripping s3ObjectKey so the payee workflow
    // generates a fresh key and avoids unique constraint conflicts.
    const sanitizedInitialDocument: PayeeBaseSupportingDocument | undefined = initialDocument
      ? { ...initialDocument, s3ObjectKey: "" }
      : undefined;

    return (
      <SlideoutDrawerV2
        isOpen={isOpen}
        onClose={handleExit}
        // @ts-expect-error Temporarily allow string literal for direction
        direction="bottom"
        data-testid="payee-creation-drawer"
      >
        <div className={customHeader}>
          <Button variant="muted" icon="arrow-left" iconPosition="left" onClick={handleExit}>
            Back to payable details
          </Button>
          <div className={headerTitleOverlay}>
            <Text variant="strong-lg">{`Create New Payee · ${selectedType}`}</Text>
          </div>
        </div>
        <SlideoutDrawerV2.Body>
          <div className={bodySizeContainer}>
            <UnifiedPayeeWorkflow
              payeeType={selectedType}
              arenaId={arenaId}
              arenaName={arenaName}
              initialDocument={sanitizedInitialDocument}
              lockArena={Boolean(arenaId)}
              onPayeeCreateSuccess={handlePayeeCreationSuccess}
              onStepChange={handleStepChange}
            />
          </div>
        </SlideoutDrawerV2.Body>
      </SlideoutDrawerV2>
    );
  }

  return (
    <SlideoutDrawerV2
      isOpen={isOpen}
      onClose={onClose}
      // @ts-expect-error Temporarily allow string literal for direction
      direction="bottom"
      data-testid="payee-creation-drawer"
    >
      <div className={customHeader}>
        <Button variant="muted" icon="arrow-left" iconPosition="left" onClick={onClose}>
          Back to payable details
        </Button>
        <div className={headerTitleOverlay}>
          <Text variant="strong-lg">Create new payee</Text>
        </div>
      </div>
      <SlideoutDrawerV2.Body>
        <div className={bodySizeContainer}>
          <Flex justifyContent="center">
            <div className={slideoutDrawerContainer}>
              <Card title="Create a new payee">
                <Text variant="strong-md">Select the payee type</Text>

                <Flex flexDirection="column" gap={16}>
                  <SelectableCards
                    value={selectedPayeeType ?? undefined}
                    onSelectOption={(val: string | number) => {
                      if (isPayeeType(val)) setSelectedPayeeType(val);
                    }}
                    options={[
                      { title: "Vendor", icon: "briefcase", value: "Vendor" },
                      { title: "Entity", icon: "building", value: "Entity" },
                      { title: "Investment", icon: "chart-line", value: "Investment" },
                    ]}
                  />
                </Flex>
              </Card>
            </div>
          </Flex>
        </div>
      </SlideoutDrawerV2.Body>
      <SlideoutDrawerV2.ComplexFooter
        primaryButton={{
          label: "Continue",
          disabled: !selectedPayeeType,
          onClick: handleContinueFromTypeSelection,
        }}
      />
    </SlideoutDrawerV2>
  );
};
